const escapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character],
  );

const dateValue = (value) => {
  if (!value) return new Date();
  if (typeof value?.toDate === "function") return value.toDate();
  if (Number.isFinite(value?.seconds)) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const dateText = (value) =>
  dateValue(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

const multiline = (value) =>
  escapeHtml(value || "").replace(/\r?\n/g, "<br>");

const loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Selected signature image is invalid."));
    image.src = dataUrl;
  });

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Signature file could not be read."));
    reader.readAsDataURL(file);
  });

export async function prepareSignatureImage(file) {
  if (!file) throw new Error("Please select a signature image.");
  if (!String(file.type || "").startsWith("image/")) {
    throw new Error("Only PNG, JPG or WEBP signature images are allowed.");
  }
  if (Number(file.size || 0) > 5 * 1024 * 1024) {
    throw new Error("Signature image must be smaller than 5 MB.");
  }

  const source = await readFile(file);
  const image = await loadImage(source);
  const maxWidth = 900;
  const maxHeight = 320;
  const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  context.clearRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  let dataUrl = canvas.toDataURL("image/png");
  if (dataUrl.length > 480000) {
    context.globalCompositeOperation = "destination-over";
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    dataUrl = canvas.toDataURL("image/jpeg", 0.86);
  }
  if (dataUrl.length > 700000) {
    throw new Error("Signature remains too large after compression. Use a cropped signature image.");
  }
  return dataUrl;
}

export function buildHiringAssuranceHtml(letter = {}, options = {}) {
  const logoUrl =
    options.logoUrl ||
    (typeof window !== "undefined"
      ? new URL("/images/nafed-logo.png", window.location.origin).href
      : "/images/nafed-logo.png");
  const terms = Array.isArray(letter.terms) ? letter.terms : [];
  const issueDate = dateText(letter.issueDate || letter.issuedAt);
  const capacity = Number(letter.capacityMt || 0);
  const address = [
    letter.village,
    letter.taluka,
    letter.district,
    letter.state,
  ]
    .filter(Boolean)
    .join(", ");

  return `<!doctype html>
  <html><head><meta charset="utf-8"><title>${escapeHtml(letter.letterNumber || "Hiring Assurance")}</title>
  <style>
  @page{size:A4;margin:14mm}*{box-sizing:border-box}body{margin:0;background:#fff;color:#14283f;font-family:"Times New Roman",Georgia,serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}.sheet{min-height:267mm;position:relative;padding:0 2mm 22mm}.head{display:grid;grid-template-columns:92px 1fr 92px;align-items:center;padding:4px 0 12px;border-bottom:3px solid #0b5a3c}.head img{width:84px;height:72px;object-fit:contain}.head-copy{text-align:center}.head-copy h1{margin:0;color:#0a3864;font-family:Arial,sans-serif;font-size:20px;letter-spacing:.02em}.head-copy h2{margin:5px 0 0;color:#0b5a3c;font-family:Arial,sans-serif;font-size:13px}.head-copy p{margin:4px 0 0;color:#53677b;font-family:Arial,sans-serif;font-size:9px}.gov-mark{text-align:right;color:#0a3864;font-family:Arial,sans-serif;font-size:9px;font-weight:700}.meta{display:flex;justify-content:space-between;gap:18px;margin-top:16px;font-size:12px}.to{margin-top:18px;font-size:13px;line-height:1.45}.subject{margin:18px 0 14px;padding:9px 12px;border-left:4px solid #0b5a3c;background:#edf7f1;font-size:13px;line-height:1.45}.body{font-size:13px;line-height:1.7;text-align:justify}.details{width:100%;margin:15px 0;border-collapse:collapse;font-family:Arial,sans-serif;font-size:10.5px}.details th,.details td{padding:8px;border:1px solid #7d8c99;text-align:left;vertical-align:top}.details th{width:28%;background:#eef3f7;color:#0a3864}.terms{margin:12px 0 0;padding-left:21px;font-size:11.5px;line-height:1.55}.terms li{margin-bottom:5px}.signature{display:flex;justify-content:flex-end;margin-top:30px}.signature-block{width:255px;text-align:center;font-family:Arial,sans-serif}.signature-block img{display:block;width:190px;height:70px;margin:0 auto 3px;object-fit:contain}.signature-block b{display:block;font-size:11px}.signature-block span{display:block;margin-top:3px;font-size:9.5px;color:#526579}.verification{margin-top:24px;padding:9px;border:1px dashed #8597a6;background:#fafcfd;color:#526579;font-family:Arial,sans-serif;font-size:8.5px}.footer{position:absolute;right:0;bottom:0;left:0;padding-top:8px;border-top:1px solid #9fafbc;color:#657789;font-family:Arial,sans-serif;font-size:8px;text-align:center}@media print{body{background:#fff}.sheet{min-height:267mm}}
  </style></head><body><main class="sheet">
    <header class="head"><img src="${escapeHtml(logoUrl)}" alt="NAFED"><div class="head-copy"><h1>National Agricultural Cooperative Marketing Federation of India Ltd.</h1><h2>${escapeHtml(letter.state || "State")} State Branch</h2><p>${escapeHtml(letter.branchOfficeAddress || "NAFED State Branch Office")}</p></div><div class="gov-mark">Government of India<br>Ministry of Agriculture<br>&amp; Farmers Welfare</div></header>
    <div class="meta"><span><b>Reference No.:</b> ${escapeHtml(letter.letterNumber || "-")}</span><span><b>Date:</b> ${escapeHtml(issueDate)}</span></div>
    <div class="to"><b>To,</b><br><b>${escapeHtml(letter.pacsName || "PACS / Society")}</b><br>${escapeHtml(address || letter.state || "-")}<br>Registration No.: ${escapeHtml(letter.registrationNumber || "-")}</div>
    <div class="subject"><b>Subject:</b> ${escapeHtml(letter.subject || "Hiring Assurance for the proposed scientific storage godown")}</div>
    <div class="body">Dear Sir/Madam,<br><br>${multiline(letter.bodyText)}</div>
    <table class="details"><tr><th>PACS / Society</th><td>${escapeHtml(letter.pacsName || "-")}</td></tr><tr><th>Proposed Capacity</th><td>${capacity ? `${capacity.toLocaleString("en-IN")} MT` : "As per approved DPR"}</td></tr><tr><th>Location</th><td>${escapeHtml(address || "-")}</td></tr><tr><th>Allocated PMC</th><td>${escapeHtml(letter.allocatedPmcName || "-")}</td></tr><tr><th>Indicative Hiring Period</th><td>${escapeHtml(letter.hiringPeriod || "As per applicable scheme / final agreement")}</td></tr></table>
    ${terms.length ? `<ol class="terms">${terms.map((term) => `<li>${escapeHtml(term)}</li>`).join("")}</ol>` : ""}
    <div class="signature"><div class="signature-block">${letter.signatureDataUrl ? `<img src="${escapeHtml(letter.signatureDataUrl)}" alt="Authorised signature">` : ""}<b>${escapeHtml(letter.signerName || "Authorised Signatory")}</b><span>${escapeHtml(letter.signerDesignation || "State Branch")}</span><span>NAFED · ${escapeHtml(letter.state || "State")} State Branch</span></div></div>
    <div class="verification">This digitally generated letter is available in the PACS portal against Document ID <b>${escapeHtml(letter.documentId || letter.pacsUid || "-")}</b>. Any alteration outside the portal copy shall render the altered copy invalid.</div>
    <footer class="footer">System-generated official document · NAFED State Branch Portal · ${escapeHtml(letter.letterNumber || "Hiring Assurance")}</footer>
  </main></body></html>`;
}

export function printHiringAssurance(letter = {}) {
  const reportWindow = window.open(
    "",
    "_blank",
    "width=980,height=900,scrollbars=yes,resizable=yes",
  );
  if (!reportWindow) {
    throw new Error("Document window was blocked. Please allow pop-ups and try again.");
  }
  reportWindow.document.open();
  reportWindow.document.write(
    buildHiringAssuranceHtml(letter).replace(
      "</body>",
      '<script>window.onload=function(){setTimeout(function(){window.print();},450)};<\/script></body>',
    ),
  );
  reportWindow.document.close();
}
